import React, { useCallback, useEffect, useState } from 'react';
import MetaTags from 'components/common/MetaTags';
import {
  getSeasons,
  addSeason,
  updateSeason,
  deleteSeason,
  startNewSeason,
} from 'api/season';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styles from './Season.module.css';
import MainContent from 'components/NewDesign/MainContent';
import PageHeader from 'components/NewDesign/PageHeader';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import SeasonsTable from 'components/admin/season/SeasonsTable';
import SeasonFormModal from 'components/admin/season/SeasonFormModal';
import SeasonStartProgressModal from 'components/admin/season/SeasonStartProgressModal';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm/CustomNewConfirm';
import PinProtectedAction from 'components/common/PinProtected/PinProtectedAction';
import { getSeasonBusinessRules } from 'services/admin/SeasonService';
import { getClubSettings } from 'api/clubApi';
import useGroupLabels from 'hooks/useGroupLabels';
import { organisationWordOf } from 'utils/clubFeatures';
import { pendingRecordsRolloverLine } from 'utils/seasonRollover';

const Season = () => {
  const { selectedClub, clubRecordsPendingCount } = useSelector((state) => state.user || {});
  // What a new season does to each athlete differs for a club that groups by year level: no ages are
  // calculated, and every student moves up one year level. See groupPhrases in utils/groupLabel.js.
  const { phrases } = useGroupLabels();
  const [loading, setLoading] = useState(true);
  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState(null);
  const [draftSeason, setDraftSeason] = useState(null);
  const [clubPin, setClubPin] = useState('');

  const [selectedSeason, setSelectedSeason] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showStartConfirmModal, setShowStartConfirmModal] = useState(false);
  const [rules, setRules] = useState(null);

  // Season rollover progress: idle | running | success | error. The rollover
  // can take up to a minute, so it gets a dedicated progress modal instead of
  // blanking the page with the global loading spinner.
  const [startProgress, setStartProgress] = useState({
    status: 'idle',
    seasonName: '',
    result: null,
    error: '',
  });

  // Fetch club settings to get the PIN
  const fetchClubPin = useCallback(async () => {
    try {
      if (selectedClub?.clubID) {
        const response = await getClubSettings(selectedClub.clubID);
        setClubPin(response.data.adminPIN);
      }
    } catch (err) {
      console.error('[Season] Fetch club PIN error: ', err);
    }
  }, [selectedClub]);

  useEffect(() => {
    fetchClubPin();
  }, [fetchClubPin]);

  const closeEditPopup = () => {
    setSelectedSeason(null);
    setShowPopup(false);
  };

  const closeDeleteModal = () => {
    setSelectedSeason(null);
    setShowDeleteConfirmModal(false);
  };

  const fetchSeasons = useCallback(async () => {
    try {
      if (selectedClub) {
        const response = await getSeasons(selectedClub?.clubID || '');
        const seasonsData = response.data || [];
        const { activeSeason, draftSeason, sortedSeasons, rules } =
          getSeasonBusinessRules(seasonsData);
        setActiveSeason(activeSeason);
        setDraftSeason(draftSeason);
        setSeasons(sortedSeasons);
        setRules(rules);
        setLoading(false);
      }
    } catch (err) {
      console.error('[Season] Fetch seasons error: ', err);
      toast.error('Failed to load seasons');
      setLoading(false);
    }
  }, [selectedClub]);

  useEffect(() => {
    fetchSeasons();
  }, [fetchSeasons]);

  // PIN-protected action handlers
  const handleAddSeasonWithPin = async (data, pin) => {
    if (!rules?.canAddNewSeason) {
      toast.warning('You can only have one draft season at a time');
      return;
    }
    setSelectedSeason(null);
    setShowPopup(true);
  };

  const handleEditSeasonWithPin = async (season, pin) => {
    if (season.status === 'historical') {
      toast.info('Historical seasons cannot be edited');
      return;
    }
    setSelectedSeason(season);
    setShowPopup(true);
  };

  const handleDeleteSeasonWithPin = async (season, pin) => {
    if (season.status !== 'Draft') {
      toast.info('Only draft seasons can be deleted');
      return;
    }
    setSelectedSeason(season);
    setShowDeleteConfirmModal(true);
  };

  // Step 1 of the rollover flow: gate on the business rules, then show the
  // detailed confirmation so the admin knows exactly what the rollover does
  // before the PIN is even requested.
  const handleStartNewSeasonClick = () => {
    if (!rules?.canStartNewSeason) {
      if (!draftSeason) {
        toast.warning('No draft season exists to activate');
      } else {
        toast.warning(
          `Current season must end before starting a new one (ends ${activeSeason.endDate})`
        );
      }
      return;
    }
    setShowStartConfirmModal(true);
  };

  // Step 3: runs only after the confirmation dialog and server-side PIN
  // validation have both passed.
  const handleStartNewSeasonWithPin = async (data, pin) => {
    if (!draftSeason) {
      toast.warning('No draft season exists to activate');
      return;
    }

    // Capture the name now: fetchSeasons() clears draftSeason once the
    // rollover succeeds, but the modal still needs it.
    const seasonName = draftSeason.name;
    setStartProgress({ status: 'running', seasonName, result: null, error: '' });
    try {
      const response = await startNewSeason(
        selectedClub.clubID,
        draftSeason.seasonID,
        pin
      );
      await fetchSeasons();
      setStartProgress({
        status: 'success',
        seasonName,
        result: response.data,
        error: '',
      });
    } catch (err) {
      console.error('Start new season error:', err);
      setStartProgress({
        status: 'error',
        seasonName,
        result: null,
        error: err.response?.data?.message || 'Failed to start new season',
      });
    }
  };

  const handleSeasonSave = async (seasonData) => {
    try {
      setLoading(true);
      const payload = {
        ...seasonData,
        clubId: selectedClub.clubID,
        authorizationPin: clubPin, // Use the stored PIN
      };
      if (selectedSeason) {
        const response = await updateSeason(selectedSeason.seasonID, payload);
        console.log('update season', response);
        toast.success('Season updated successfully');
      } else {
        const response = await addSeason(payload);
        console.log('add season', response);
        toast.success('Draft season created successfully');
      }
      await fetchSeasons();
      setShowPopup(false);
    } catch (err) {
      console.error('Season save error:', err);
      toast.error(err.response?.data?.message || 'Failed to save season');
    } finally {
      setLoading(false);
    }
  };

  const handleSeasonDelete = async () => {
    try {
      setLoading(true);
      setShowDeleteConfirmModal(false);
      await deleteSeason(selectedClub.clubID, selectedSeason.seasonID, clubPin);
      toast.success('Draft season deleted');
      await fetchSeasons();
    } catch (err) {
      console.error('Season delete error:', err);
      toast.error(err.response?.data?.message || 'Failed to delete season');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <MetaTags title="SplitFast | Season" />
      <MainContent loading={loading}>
        <PinProtectedAction clubId={selectedClub?.clubID} currentPin={clubPin}>
          {({ requestPinValidation }) => (
            <>
              <PageHeader
                subTitle={`${seasons?.length} Seasons`}
                title="Manage Seasons"
                primarySubTitle
                regularWeightSubTitle
              >
                <div className={styles.buttonContainer}>
                  <CustomNewButton
                    label="+ Add Season"
                    tooltip={rules?.toolTipAddSeason || ''}
                    onClick={() =>
                      requestPinValidation(
                        'add-season',
                        handleAddSeasonWithPin,
                        null,
                        'Creating seasons is restricted to admin users with PIN code access. Enter the admin PIN to create a new draft season.'
                      )
                    }
                    variant="primary"
                    disabled={!rules?.canAddNewSeason}
                  />
                  <CustomNewButton
                    label="Start New Season"
                    tooltip={rules?.toolTipStartNewSeason || ''}
                    onClick={handleStartNewSeasonClick}
                    variant="success"
                    disabled={!rules?.canStartNewSeason}
                  />
                </div>
              </PageHeader>

              {activeSeason && (
                <div className={styles.statusInfo}>
                  <div className={styles.statusInfoTitle}>
                    Current Active Season
                  </div>
                  <div className={styles.statusInfoText}>
                    <strong>{activeSeason.name}</strong> is currently active.{' '}
                    {phrases.hasAgeCalculationDate && (
                      <>
                        All athlete competition ages are calculated based on{' '}
                        {activeSeason.ageCalculationDate}.{' '}
                      </>
                    )}
                    This season runs until {activeSeason.endDate}.
                  </div>
                </div>
              )}

              <CustomNewConfirm
                isOpen={showStartConfirmModal}
                onClose={() => setShowStartConfirmModal(false)}
                title="Start New Season"
                description="Starting a new season runs a club-wide rollover on the server. Review what will happen before you continue:"
                confirmButtonText="Continue to PIN Entry"
                confirmButtonVariant="danger"
                showWarning={true}
                warningText="Once the rollover starts it cannot be stopped, and once complete it cannot be reversed."
                onConfirm={() => {
                  setShowStartConfirmModal(false);
                  requestPinValidation(
                    'start-new-season',
                    handleStartNewSeasonWithPin,
                    null,
                    'Enter the admin PIN to authorize the season rollover.'
                  );
                }}
              >
                <ul className={styles.rolloverEffectsList}>
                  {activeSeason && (
                    <li>
                      <strong>{activeSeason.name}</strong> will be archived as
                      a historical season
                    </li>
                  )}
                  <li>
                    <strong>{draftSeason?.name}</strong> becomes the club&apos;s
                    active season
                  </li>
                  <li>
                    {phrases.seasonRollover}
                  </li>
                  <li>
                    Chat group messages from the previous season are
                    cleared
                  </li>
                  <li>
                    {phrases.seasonChat}
                  </li>
                  <li>
                    {pendingRecordsRolloverLine(clubRecordsPendingCount, organisationWordOf(selectedClub))}
                  </li>
                </ul>
              </CustomNewConfirm>

              <SeasonsTable
                seasons={seasons}
                activeSeasonId={activeSeason?.seasonID}
                handleEdit={(season) =>
                  requestPinValidation(
                    'edit-season',
                    handleEditSeasonWithPin,
                    season,
                    `Editing season "${season.name}" requires an admin PIN code.`
                  )
                }
                handleDelete={(season) =>
                  requestPinValidation(
                    'delete-season',
                    handleDeleteSeasonWithPin,
                    season,
                    `Deleting season "${season.name}" requires an admin PIN code. This cannot be undone.`
                  )
                }
              />
            </>
          )}
        </PinProtectedAction>
      </MainContent>

      <SeasonFormModal
        isOpen={showPopup}
        onClose={closeEditPopup}
        season={selectedSeason}
        activeSeason={activeSeason}
        onSave={handleSeasonSave}
      />

      <SeasonStartProgressModal
        isOpen={startProgress.status !== 'idle'}
        status={startProgress.status}
        seasonName={startProgress.seasonName}
        result={startProgress.result}
        error={startProgress.error}
        onClose={() =>
          setStartProgress({
            status: 'idle',
            seasonName: '',
            result: null,
            error: '',
          })
        }
      />

      <CustomNewConfirm
        isOpen={showDeleteConfirmModal}
        onClose={closeDeleteModal}
        title="Delete Season"
        onConfirm={handleSeasonDelete}
        confirmButtonText="Delete Season"
      >
        <div className={styles.modalDescription}>
          Are you sure you want to delete the draft season{' '}
          <strong>{selectedSeason?.name}</strong>?
        </div>
        <div className={styles.dangerText}>
          ⚠️ This action cannot be undone.
        </div>
      </CustomNewConfirm>
    </>
  );
};

export default Season;
